import IntegrationServices from "const/IntegrationServices";
import moment from "moment";

const UPSELL_MAX_OLDEST_YEARS = 10;

export default class Businesses {
  static getDefaultRpaStartFromMinDate(integrationService, createdAt) {
    if (integrationService === IntegrationServices.QUICK_BOOKS.value) {
      const businessMinDate = moment(createdAt).startOf("year");

      return moment.max(businessMinDate, moment().subtract(1, "year").startOf("year"));
    }

    return moment().subtract(1, "year").startOf("year");
  }

  static getBookCloseDate(quickBooksBookCloseDate) {
    if (quickBooksBookCloseDate && moment(quickBooksBookCloseDate).isValid()) {
      return moment(quickBooksBookCloseDate);
    }

    return null;
  }

  static getRpaStartFromMinDate(integrationService, rpaUpsellDate, createdAt) {
    if (rpaUpsellDate && moment(rpaUpsellDate).isValid()) {
      return moment(rpaUpsellDate).startOf("month");
    }

    return Businesses.getDefaultRpaStartFromMinDate(integrationService, createdAt);
  }

  static getUpsellDate(quickBooksOldestTransactionDate, bookCloseDate) {
    const oldestTransactionDate = quickBooksOldestTransactionDate && moment(quickBooksOldestTransactionDate).isValid()
      ? moment(quickBooksOldestTransactionDate).startOf("month")
      : null;

    const openBookDate = bookCloseDate && moment(bookCloseDate).isValid()
      ? moment(bookCloseDate).add(1, "month").startOf("month")
      : null;

    return oldestTransactionDate && openBookDate
      ? moment.max(oldestTransactionDate, openBookDate)
      : oldestTransactionDate || openBookDate || null;
  }

  static checkIsOnboarding(extraData, settings = {}) {
    const {
      uncategorizedRpaEnabled,
      lastGptAiCategorizedAt,
      lastEntitiesAutoSetAt
    } = extraData || {};

    return (uncategorizedRpaEnabled === undefined
      ? !lastEntitiesAutoSetAt
      : (
        !lastGptAiCategorizedAt
        || uncategorizedRpaEnabled === false
        || settings.rpaAutoAskClient === undefined
      ));
  }

  static checkIsRpaMode(extraData) {
    const { uncategorizedRpaEnabled } = extraData || {};

    return uncategorizedRpaEnabled !== undefined;
  }

  static checkUpsellAvailable({
    createdAt,
    integrationService,
    rpaStartFrom,
    rpaUpsellDate,
    uncategorizedRpaEnabled,
    quickBooksOldestTransactionDate,
    quickBooksBookCloseDate
  } = {}) {
    if (integrationService !== IntegrationServices.QUICK_BOOKS.value) return false;
    if (
      !quickBooksOldestTransactionDate
      || !moment(quickBooksOldestTransactionDate).isValid()
      || moment(quickBooksOldestTransactionDate).isBefore(moment().subtract(UPSELL_MAX_OLDEST_YEARS, "year").startOf("year"))
    ) {
      return false;
    }

    const startFromDate = Businesses.getUpsellDate(
      quickBooksOldestTransactionDate,
      Businesses.getBookCloseDate(quickBooksBookCloseDate)
    );

    if (!startFromDate) return false;
    if (uncategorizedRpaEnabled === undefined) return false;

    const currentRpaStartFromDate = rpaStartFrom && moment(rpaStartFrom).isValid()
      ? moment(rpaStartFrom).startOf("month")
      : Businesses.getRpaStartFromMinDate(integrationService, rpaUpsellDate, createdAt);

    return !rpaUpsellDate
      && currentRpaStartFromDate.isAfter(startFromDate)
      && startFromDate.isBefore(Businesses.getDefaultRpaStartFromMinDate(integrationService, createdAt));
  }

  static checkRpaSettingsRequiredFields({
    createdAt = null,
    integrationService = "",
    rpaUpsellDate = null,
    rpaAmountDocumentRule = "",
    rpaClientEmail = "",
    rpaExcludedAccounts = [],
    rpaExcludedWords = [],
    rpaStartFrom = "",
    rpaTransactionTypes = "",
    shouldRequireAskPerson = false,
    shouldRequireExcludedAccounts = false,
    shouldRequireExcludedWords = false,
    shouldRequireMinTransactionAmount = false,
    shouldRequireUncategorized = false,
    uncategorizedCategoryNames = [],
    rpaAutoAskClient = false,
    currentSavedRpaStartFrom = ""
  }) {
    const rpaStartFromMinDate = Businesses.getRpaStartFromMinDate(integrationService, rpaUpsellDate, createdAt);

    const unchangedLegacyRpaStartFrom = rpaStartFrom
      && currentSavedRpaStartFrom
      && moment(rpaStartFrom).isValid()
      && moment(currentSavedRpaStartFrom).isValid()
      && moment(rpaStartFrom).isSame(currentSavedRpaStartFrom, "month");

    if (!rpaTransactionTypes) return false;
    if (
      !rpaStartFrom
      || (moment(rpaStartFrom).startOf("month").isBefore(rpaStartFromMinDate) && !unchangedLegacyRpaStartFrom)
    ) {
      return false;
    }
    if (shouldRequireUncategorized && !uncategorizedCategoryNames?.length) return false;
    if (shouldRequireAskPerson && rpaAutoAskClient && !rpaClientEmail) return false;
    if (shouldRequireExcludedAccounts && !rpaExcludedAccounts?.length) return false;
    if (shouldRequireExcludedWords && !rpaExcludedWords?.length) return false;
    if (shouldRequireMinTransactionAmount && rpaAmountDocumentRule === "") return false;

    return true;
  }
}
